import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const PermissionContext = createContext(null);

export const usePermission = () => {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    // Fallback: no context means permissions not loaded (or not needed, e.g. login page)
    return { hasPermission: () => true, hasAnyPermission: () => true, isLoading: false };
  }
  return ctx;
};

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setPermissions({});
      setIsSuperAdmin(false);
      setIsLoading(false);
      return;
    }

    try {
      const res = await fetch('/api/employees/my-permissions', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Handle 401: token expired/invalid, redirect to login
      if (res.status === 401) {
        console.warn('PermissionContext: 401 Unauthorized, clearing token and redirecting to login');
        localStorage.removeItem('token');
        window.location.href = '/login';
        return;
      }
      
      const data = await res.json();
      if (data.status === 'success') {
        setIsSuperAdmin(data.data.is_super_admin);
        setPermissions(data.data.permissions);
      } else {
        setPermissions({});
      }
    } catch (err) {
      console.error('Failed to fetch permissions:', err);
      setPermissions({});
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const hasPermission = useCallback((resource, privilege) => {
    // Super admin has all permissions
    if (isSuperAdmin) return true;
    if (!permissions || !resource) return false;
    const privs = permissions[resource];
    if (!privs) return false;
    if (!privilege) return true; // If no privilege specified, just check resource existence
    return privs.includes(privilege);
  }, [permissions, isSuperAdmin]);

  const hasAnyPermission = useCallback((resource, privileges) => {
    if (isSuperAdmin) return true;
    if (!resource) return false;
    return privileges.some(p => hasPermission(resource, p));
  }, [hasPermission, isSuperAdmin]);

  // Check if user has permission on any resource from a list
  const hasAnyResource = useCallback((resources) => {
    if (isSuperAdmin) return true;
    if (!permissions) return false;
    return resources.some(r => permissions[r] && permissions[r].length > 0);
  }, [permissions, isSuperAdmin]);

  return (
    <PermissionContext.Provider value={{
      permissions,
      isSuperAdmin,
      isLoading,
      hasPermission,
      hasAnyPermission,
      hasAnyResource,
      refreshPermissions: fetchPermissions,
    }}>
      {children}
    </PermissionContext.Provider>
  );
};

export default PermissionContext;
